import {
  SalesTaxFilingFrequencyEnum,
  ServiceRequestStatus,
} from '@commenda/prisma';
import { TaskGroupDetails } from '@commenda/types/src/service-request/types';

export enum FilingFrequency {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  QUARTERLY_PREPAY_MONTHLY = 'QUARTERLY_PREPAY_MONTHLY',
  SEMI_ANNUALLY = 'SEMI_ANNUALLY',
  ANNUAL_CALENDAR_YEAR = 'ANNUAL_CALENDAR_YEAR',
}

export enum FilingWorkflowStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ARCHIVED = 'ARCHIVED',
}

export enum FilingReviewStatus {
  ACCRUING = 'ACCRUING',
  ACTIVE = 'ACTIVE',
  FILED = 'FILED',
  OVERDUE = 'OVERDUE',
}

export const FilingReviewStatusNameMap = {
  [FilingReviewStatus.ACCRUING]: 'Accruing',
  [FilingReviewStatus.ACTIVE]: 'Active',
  [FilingReviewStatus.FILED]: 'Filed',
  [FilingReviewStatus.OVERDUE]: 'Overdue',
};

export const FilingReviewStatusColorMap = {
  [FilingReviewStatus.ACCRUING]: 'gray',
  [FilingReviewStatus.ACTIVE]: 'blue',
  [FilingReviewStatus.FILED]: 'success',
  [FilingReviewStatus.OVERDUE]: 'error',
};

export const FilingWorkflowStatusNameMap = {
  [FilingWorkflowStatus.NOT_STARTED]: 'Not Started',
  [FilingWorkflowStatus.IN_PROGRESS]: 'In Progress',
  [FilingWorkflowStatus.COMPLETED]: 'Completed',
  [FilingWorkflowStatus.ARCHIVED]: 'Archived',
};

export const FilingWorkflowStatusColorMap = {
  [FilingWorkflowStatus.NOT_STARTED]: 'gray',
  [FilingWorkflowStatus.IN_PROGRESS]: 'warning',
  [FilingWorkflowStatus.COMPLETED]: 'success',
  [FilingWorkflowStatus.ARCHIVED]: 'error',
};

export const FilingFrequencyNameMap = {
  [SalesTaxFilingFrequencyEnum.MONTHLY]: 'Monthly',
  [SalesTaxFilingFrequencyEnum.QUARTERLY]: 'Quarterly',
  [SalesTaxFilingFrequencyEnum.QUARTERLY_PREPAY_MONTHLY]:
    'Quarterly Prepay Monthly',
  [SalesTaxFilingFrequencyEnum.SEMI_ANNUALLY]: 'Semi-Annually',
  [SalesTaxFilingFrequencyEnum.ANNUAL_CALENDAR_YEAR]: 'Annual Calendar Year',
  [SalesTaxFilingFrequencyEnum.FISCAL_YEAR]: 'Fiscal Year',
  [SalesTaxFilingFrequencyEnum.MONTHLY_ACCELERATED_PREPAY_EARLY]:
    'Monthly Accelerated Prepay Early',
};

export interface BaseAssignee {
  id: string;
  name: string;
  email: string;
}

export interface AccountingPartner extends BaseAssignee {
  firm: string;
}

export interface CorporationDataForFiling {
  id: number;
  companyDisplayName: string;
  incorporationCountry: string;
}

export interface ServiceRequestDataForFiling {
  id: string;
  status: ServiceRequestStatus;
  metadata: unknown;
  taskGroups: TaskGroupDetails[];
  // Additional fields needed by task components
  salesTaxFilingId?: string;
  corporationId?: number;
  companyId?: number;
  ChatRoom?: {
    id: string;
  };
}

// filing record
export interface Filing<T = unknown> {
  id: string;
  filing_id: string;
  filing_locked_at?: string | Date | null;
  jurisdiction: string;
  filingName: string;
  filingPeriod: string; // YYYY-MM or YYYY-Q#
  frequency: (typeof FilingFrequencyNameMap)[keyof typeof FilingFrequencyNameMap];
  dueDate: string; // ISO date string
  filingStatus: (typeof FilingReviewStatusNameMap)[keyof typeof FilingReviewStatusNameMap];
  filingWorkflowStatus: (typeof FilingWorkflowStatusNameMap)[keyof typeof FilingWorkflowStatusNameMap];
  filedBy: string;
  assignedResponsibleAgent: string;
  filedAt: string; // ISO date string
  commendapediaId: string;
  assignees: BaseAssignee[];
  agent?: BaseAssignee;
  filedByAgent?: BaseAssignee;
  corporation: CorporationDataForFiling;
  lastUpdated: string; // ISO date string
  serviceRequest?: ServiceRequestDataForFiling;
  metadata?: Partial<T>;
}
